import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Globe2, Mail, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { BDC_PHONE_DISPLAY, BDC_PHONE_HREF } from "@/config/brand";

const HEADER_IMG =
  "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=80";

const DealerProfile = () => {
  const { id } = useParams();
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/dealers/${id}`).then((r) => setDealer(r.data)).catch(() => setDealer(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="h-80 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!dealer) {
    return (
      <div className="max-w-3xl mx-auto text-center py-24">
        <p className="font-display text-2xl text-slate-800">Dealership not found</p>
        <Button asChild className="mt-6 bg-brand hover:bg-brand-hover text-white">
          <Link to="/dealers">
            <ArrowLeft size={16} /> Back to dealerships
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={dealer.logo_url || HEADER_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <Link to="/dealers" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand">
            <ArrowLeft size={14} /> All dealerships
          </Link>
          <p className="mt-6 text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Partner dealership · {dealer.city}, MA
          </p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
            {dealer.name}
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">{dealer.description}</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
            <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-brand" /> {dealer.address}, {dealer.city}, {dealer.state} {dealer.zip}</span>
            <span className="inline-flex items-center gap-2"><Phone size={14} className="text-brand" /> {dealer.phone}</span>
            <span className="inline-flex items-center gap-2"><Mail size={14} className="text-brand" /> {dealer.email}</span>
            {dealer.website && <span className="inline-flex items-center gap-2"><Globe2 size={14} className="text-brand" /> {dealer.website.replace(/^https?:\/\//, "")}</span>}
            {dealer.hours && <span className="inline-flex items-center gap-2"><Clock size={14} className="text-brand" /> {dealer.hours}</span>}
          </div>
          <div className="mt-6 inline-flex items-center gap-3 bg-white/95 backdrop-blur border border-slate-200 rounded-full px-4 py-2 shadow-sm">
            <span className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Interested?</span>
            <a
              href={BDC_PHONE_HREF}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-hover"
              data-testid="dealer-profile-bdc-phone-link"
            >
              <Phone size={13} /> Call BDC · {BDC_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {dealer.is_coming_soon && (
          <div
            className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50/70 px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3"
            data-testid="dealer-coming-soon-banner"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1">
              Coming Soon
            </span>
            <p className="text-sm text-slate-700">
              This dealership is joining the Belomotors network shortly. Inventory shown below is a preview — appointment
              booking will open when the dealer is live.
            </p>
          </div>
        )}
        <div className="flex items-end justify-between">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">
            {dealer.is_coming_soon ? "Sample inventory" : "Available inventory"}
            <span className="ml-2 text-sm text-slate-500 font-normal">({dealer.vehicles?.length || 0})</span>
          </h2>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="dealer-inventory-grid">
          {(dealer.vehicles || []).map((v) => (
            <VehicleCard
              key={v.id}
              vehicle={v}
              dealerName={dealer.name}
              isComingSoon={!!v.is_coming_soon || !!dealer.is_coming_soon}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default DealerProfile;
